/**
 * Super Admin Data Management feature-flag hook.
 *
 * Reads `/api/admin/data-management/flags` — a booleans-only snapshot that is
 * reachable even when the surface is disabled (super_admin only).  Powers the
 * dormant-state UI and hides permanent/bulk delete controls until their flags
 * are enabled server-side.  Never hardcode flag defaults — always call this.
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DEFAULTS = {
  SUPER_ADMIN_DATA_MANAGEMENT_ENABLED: false,
  SUPER_ADMIN_PERMANENT_DELETE_ENABLED: false,
  SUPER_ADMIN_BULK_DELETE_ENABLED: false,
  recovery_window_days: 30,
};

export function useDataMgmtFlags() {
  const [flags, setFlags] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const refresh = useCallback(() => {
    setLoading(true);
    return api
      .get("/admin/data-management/flags")
      .then((r) => setFlags({ ...DEFAULTS, ...(r.data || {}) }))
      .catch(() => setFlags(DEFAULTS))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) { setLoading(false); return; }   // admin-only endpoint — skip for anon/public visitors
    let alive = true;
    api
      .get("/admin/data-management/flags")
      .then((r) => alive && setFlags({ ...DEFAULTS, ...(r.data || {}) }))
      .catch(() => alive && setFlags(DEFAULTS))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [user]);

  return {
    flags,
    loading,
    dataManagementEnabled: !!flags.SUPER_ADMIN_DATA_MANAGEMENT_ENABLED,
    permanentDeleteEnabled: !!flags.SUPER_ADMIN_PERMANENT_DELETE_ENABLED,
    bulkDeleteEnabled: !!flags.SUPER_ADMIN_BULK_DELETE_ENABLED,
    bulkDeleteAllowedResources: Array.isArray(flags.bulk_delete_allowed_resources)
      ? flags.bulk_delete_allowed_resources : [],
    isBulkDeleteAllowedFor: (resource) => (
      !!flags.SUPER_ADMIN_BULK_DELETE_ENABLED
      && Array.isArray(flags.bulk_delete_allowed_resources)
      && flags.bulk_delete_allowed_resources.includes(resource)
    ),
    recoveryWindowDays: flags.recovery_window_days || 30,
    environment: flags.environment || "preview",
    refresh,
  };
}
